/**
 * The browser half of the bot challenge (server half: utils/bot-challenge.ts).
 *
 * When a route answers RECAPTCHA_CHALLENGE, `postWithBotChallenge` asks the
 * page to show the checkbox, waits for the visitor to tick it, and sends the
 * same request again with `challengeToken`. Plain functions and a tiny store,
 * not a context, because the login-code and email-verification senders are not
 * React code; components/BotChallengeHost.tsx subscribes and draws the popup.
 */

/** Same value as BOT_CHALLENGE_CODE on the server; kept here so no server module enters the bundle. */
export const BOT_CHALLENGE_CODE = 'RECAPTCHA_CHALLENGE';

export interface PendingChallenge {
	siteKey: string;
	/** The checkbox answer, or undefined when the visitor closed the popup. */
	resolve: (token: string | undefined) => void;
}

let pending: PendingChallenge | null = null;
const listeners = new Set<(challenge: PendingChallenge | null) => void>();

function publish(next: PendingChallenge | null): void {
	pending = next;
	listeners.forEach((listener) => listener(next));
}

/** Follow the challenge the page should show; called with the current one at once. */
export function subscribeBotChallenge(listener: (challenge: PendingChallenge | null) => void): () => void {
	listeners.add(listener);
	listener(pending);
	return () => {
		listeners.delete(listener);
	};
}

/**
 * Show the checkbox for `siteKey` and wait for the answer. A second request
 * while one is open closes the first unanswered, so two sends never stack two
 * popups.
 */
export function requestBotChallenge(siteKey: string): Promise<string | undefined> {
	pending?.resolve(undefined);
	return new Promise((resolve) => {
		const challenge: PendingChallenge = {
			siteKey,
			resolve: (token) => {
				if (pending === challenge) publish(null);
				resolve(token);
			},
		};
		publish(challenge);
	});
}

/** The checkbox key from a challenge reply, or null for any other reply. */
export async function challengeSiteKeyFrom(response: Response): Promise<string | null> {
	if (response.status !== 403) return null;
	try {
		const body = (await response.clone().json()) as { errorCode?: unknown; code?: unknown; challengeSiteKey?: unknown };
		const code = body.errorCode ?? body.code;
		return code === BOT_CHALLENGE_CODE && typeof body.challengeSiteKey === 'string' ? body.challengeSiteKey : null;
	} catch {
		return null;
	}
}

/**
 * POST `body` as JSON to `url`; on a challenge reply, show the checkbox and
 * POST again with the answer. The server judges the resend by the checkbox
 * alone, so the spent score token riding along is ignored. Closing the popup
 * returns the challenge reply itself, so the caller shows its usual error.
 */
export async function postWithBotChallenge(url: string, body: Record<string, unknown>, init?: RequestInit): Promise<Response> {
	const send = (extra: Record<string, unknown>) =>
		fetch(url, {
			method: 'POST',
			...init,
			headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
			body: JSON.stringify({ ...body, ...extra }),
		});

	const first = await send({});
	const siteKey = await challengeSiteKeyFrom(first);
	if (!siteKey) return first;

	const challengeToken = await requestBotChallenge(siteKey);
	if (!challengeToken) return first;
	return send({ challengeToken });
}
